import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppStateInterface } from "app/core/app-config/state/models/app-state.interface";
import { JwtName } from "app/core/authentication/constants";
import { TokenInterface } from "app/core/authentication/models";
import { AuthMatch } from "app/core/authentication/models/auth-match.enum";
import { AuthServiceInterface } from "app/core/authentication/services/auth-service.interface";
import {
  LoadToken,
  Login,
  LoginUuid,
  Logout,
  CheckLoginUuid,
} from "app/core/authentication/state/actions";
import {
  LicenseCheck,
  LicenseUpdate,
} from "app/core/authentication/state/actions/auth-license-check.action";
import { PasswordResetTokenValidate } from "app/core/authentication/state/actions/auth-password-reset-token-validate.action";
import { Error } from "app/core/error-handler/models";
import { UserInterface } from "app/core/user/models";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";

import { LicenseRequestInterface } from "../models/license-request.model";

@Injectable()
export class AuthService implements AuthServiceInterface {
  public adminSiteWindow: any = [];
  constructor(
    private readonly _store: Store<AppStateInterface>,
    private _httpClient: HttpClient
  ) {}

  public loadJwt(): Observable<string> {
    this._store.dispatch(new LoadToken());
    return this._store.select(({ auth }: AppStateInterface) => auth.jwt);
  }

  public licenseCheck(): Observable<boolean> {
    this._store.dispatch(new LicenseCheck());
    return this._store.select(({ auth }: AppStateInterface) => auth.isLicensed);
  }

  public licenseUpdate(licenseData: LicenseRequestInterface): void {
    this._store.dispatch(new LicenseUpdate(licenseData));
  }

  public login(username: string, password: string): Observable<UserInterface> {
    this._store.dispatch(new Login(username, password));
    return this.getLoggedInUser();
  }

  public loginUuid(
    uuid: string,
    isFirstLoad: boolean
  ): Observable<UserInterface> {
    this._store.dispatch(new LoginUuid(uuid, isFirstLoad));
    return this.getLoggedInUser();
  }

  public checkLoginUuid(uuid: string, isFirstLoad: boolean): void {
    this._store.dispatch(new CheckLoginUuid(uuid, isFirstLoad));
  }

  public logout(): void {
    this._store.dispatch(new Logout());
    localStorage.removeItem(JwtName.jwtName);
  }

  public signOut(value: boolean, url: any): Observable<any> {
    return this._httpClient
      .get<any>(url + value, { observe: "response" })
      .pipe(map((httpResponse: HttpResponse<boolean>) => httpResponse.body));
  }
  public passwordReset(username: string): any {
    const sendObj = {
      username,
    };
    return this._httpClient
      .post<boolean>("api/auth/sendresetemail", sendObj, {
        observe: "response",
      })
      .pipe(map((httpResponse: HttpResponse<boolean>) => httpResponse.body));
  }

  // public passwordUpdate(userId: string, password: string, ForcePassword: boolean = false): void {
  //   this._store.dispatch(new PasswordUpdate({ userId, password, ForcePassword }));
  // }

  public getPasswordSettingsData(): any {
    return this._httpClient.get<boolean>("api/site-settings/password");
  }

  public passwordUpdate(
    userId: string,
    password: string,
    ForcePassword: boolean = false
  ): any {
    const sendObj = {
      userId,
      password,
      ForcePassword,
    };
    return this._httpClient
      .put<boolean>(`/api/users/${userId}/updatepassword`, sendObj, {
        observe: "response",
      })
      .pipe(map((httpResponse: HttpResponse<boolean>) => httpResponse.body));
  }

  public validatePasswordResetToken(
    userId: string,
    resetToken: string
  ): Observable<boolean> {
    this._store.dispatch(
      new PasswordResetTokenValidate({ userId, resetToken })
    );
    return this._store.select(
      ({ auth }: AppStateInterface) => auth.resetTokenValidated
    );
  }

  public getJwt(): Observable<string> {
    return this._store.select(({ auth }: AppStateInterface) => auth.jwt);
  }

  public getToken(): Observable<TokenInterface> {
    return this._store.select(({ auth }: AppStateInterface) => auth.token);
  }

  public getLoggedInUser(): Observable<UserInterface> {
    return this._store.select(({ auth }: AppStateInterface) =>
      auth.token ? auth.token.loggedInUser : undefined
    );
  }
  public getLoginUserData(userId: any): Observable<any> {
    return this._httpClient.get<boolean>(`/api/users/${userId}`);
  }
  public getError(): Observable<Error> {
    return this._store.select(({ auth }: AppStateInterface) => auth.error);
  }

  public isLoggedInUserAuthorized(
    scopes: string[],
    matchType: AuthMatch = AuthMatch.SOME
  ): Observable<boolean> {
    return this.getToken().pipe(
      take(1),
      map((token: TokenInterface) => {
        return AuthService.isTokenAuthorized(token.scopes, scopes, matchType);
      })
    );
  }

  public isLoggedInUserAuthorizedSet(
    scopes: string[]
  ): Observable<{ [id: string]: boolean }> {
    return this.getToken().pipe(
      take(1),
      map((token: TokenInterface) => {
        const resultSet: { [id: string]: boolean } = {};

        for (const scope of scopes) {
          resultSet[scope] = AuthService.isTokenAuthorized(
            token.scopes,
            [scope],
            AuthMatch.SOME
          );
        }
        return resultSet;
      })
    );
  }

  public static isTokenAuthorized(
    scopes: string[],
    testScopes: string[],
    matchType: AuthMatch = AuthMatch.SOME
  ): boolean {
    if (!scopes) {
      return;
    }

    switch (matchType) {
      case AuthMatch.ALL:
        return testScopes.every((scope: string) => scopes.includes(scope));
      case AuthMatch.SOME:
        return testScopes.some((scope: string) => scopes.includes(scope));
      case AuthMatch.NONE:
        return !testScopes.some((scope: string) => scopes.includes(scope));
      default:
        return false;
    }
  }

  public getAuthenticatedUser(): Observable<UserInterface> {
    return this._store.select(
      ({ auth }: AppStateInterface) => auth.authenticatedUser
    );
  }

  public setAdminSiteWindow(window: any) {
    this.adminSiteWindow = window;
  }

  public closeAdminsiteWindow() {
    if (typeof this.adminSiteWindow["close"] == "function") {
      this.adminSiteWindow.close();
    }
  }

  public getAdminSiteWindow() {
    return this.adminSiteWindow;
  }

  public generateMFACode(userId: number): Observable<void> {
    return this._httpClient.get<void>(`/api/users/${userId}/mfa/generate`);
  }

  public validateMFACode(userId: number, code: string): Observable<boolean> {
    return this._httpClient.get<boolean>(`/api/users/${userId}/mfa/validate`, {
      params: { code },
    });
  }
}
